import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Focus } from 'lucide-react';
import { initializeGeminiChat, sendMessageToGemini, warmRagFromPublic } from '../lib/gemini-secure';
import { useChatThemeStandalone } from '../hooks/useChatTheme';
import ChatMessageSimple from './ChatMessageSimple';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';
import profileImage from '../assets/NewPic.png';

const categoryData = {
  projects: {
    label: 'Projects',
    icon: '💼',
    questions: [
      "Tell me about Triage AI and its 1,000+ users",
      "What is OpenSignl and how does the agentic research pipeline work?",
      "Tell me about the AI Safety Eval Framework you built at Oracle",
      "Show me your brain tumor segmentation research"
    ]
  },
  technical: {
    label: 'Technical',
    icon: '⚡',
    questions: [
      "What's your approach to full-stack AI architecture?",
      "How do you integrate multiple AI services in production?",
      "What are you exploring in vector databases and RAG?",
      "Explain your experience with OCI and cloud infrastructure"
    ]
  },
  experience: {
    label: 'Experience',
    icon: '🚀',
    questions: [
      "What's your professional background in AI development?",
      "Tell me about your CON-FO-DI development philosophy",
      "How many years have you been working with React/TypeScript?",
      "What's your experience with cloud platforms like OCI?"
    ]
  },
  about: {
    label: 'About Me',
    icon: '👋',
    questions: [
      "What do you do outside of work?",
      "What are your hobbies and interests?",
      "Do you do coffee chats?",
      "What causes do you care about?"
    ]
  }
};

const EnhancedAIChatWorking = ({
  onQuestionSelect,
  chatSize = 'compact',
  isFocusMode = false,
  onToggleSize,
  onToggleFocus,
  getChatIcon,
  onInputFocus,
  onInputChange
}) => {
  const { theme } = useChatThemeStandalone();
  const [chatSessions, setChatSessions] = useState({});
  const [currentChatId] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const messagesEndRef = useRef(null);

  const EXPAND_REGEX = /\b(expand|details|more|deep dive)\b/i;

  // Initialize first chat with Gemini AI
  useEffect(() => {
    const initializeFirstChat = async () => {
      try {
        console.log('EnhancedAIChatWorking: Initializing chat...');
        // Warm external context (fetch + embed + cache all sources)
        await warmRagFromPublic();
        console.log('EnhancedAIChatWorking: RAG warming complete, creating chat instance...');
        const chatInstance = await initializeGeminiChat();
        console.log('EnhancedAIChatWorking: Chat instance created successfully');
        const initialMessage = {
          id: 1,
          text: "Welcome to my portfolio! I am AI Abdarrahman, here are some highlights of my work.\n\n**Highlights:**\n• [OpenSignl](https://www.opensignl.com/): founder — research-backed content intelligence SaaS with multi-model failover\n• AI Safety Eval Framework: flagged safety gaps across 80% of stress-test scenarios at Oracle, drove **90% groundedness lift**\n• [Triage AI](https://triagedai.com): **1,000+ users**, 40% relevance lift, replaced AWS Comprehend\n\n**Next step:** Ask me about my work or projects!",
          type: 'ai',
          timestamp: new Date()
        };

        setChatSessions({
          1: {
            id: 1,
            instance: chatInstance,
            messages: [initialMessage],
            title: 'Main Chat'
          }
        });

        // Chat initialized successfully
        console.log('EnhancedAIChatWorking: Chat initialization successful');
      } catch (error) {
        console.error('EnhancedAIChatWorking: Failed to initialize chat:', error);
        console.error('EnhancedAIChatWorking: Error details:', error.message);

        // Fallback message when initialization fails
        const fallbackMessage = {
          id: 1,
          text: "Welcome to my portfolio! I am AI Abdarrahman, here are some highlights of my work.\n\n**Highlights:**\n• [OpenSignl](https://www.opensignl.com/): founder — research-backed content intelligence SaaS with multi-model failover\n• AI Safety Eval Framework: flagged safety gaps across 80% of stress-test scenarios at Oracle, drove **90% groundedness lift**\n• [Triage AI](https://triagedai.com): **1,000+ users**, 40% relevance lift, replaced AWS Comprehend\n\n**Next step:** Ask me about my work or projects!",
          type: 'ai',
          timestamp: new Date()
        };

        setChatSessions({
          1: {
            id: 1,
            instance: null,
            messages: [fallbackMessage],
            title: 'Main Chat'
          }
        });
      }
    };

    initializeFirstChat();
  }, []);

  // Auto-scroll to top for initial message, bottom for new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      const chatContainer = messagesEndRef.current.closest('.chat-messages');
      if (chatContainer) {
        const currentMessages = chatSessions[currentChatId]?.messages || [];
        // If this is the first message (welcome), scroll to top
        if (currentMessages.length === 1) {
          chatContainer.scrollTop = 0;
        } else {
          // For new messages, scroll to bottom
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      }
    }
  }, [chatSessions, currentChatId, isTyping]);

  const addMessage = (chatId, text, type) => {
    const newMessage = {
      id: Date.now() + Math.random(),
      text,
      type,
      timestamp: new Date()
    };

    setChatSessions(prev => ({
      ...prev,
      [chatId]: {
        ...prev[chatId],
        messages: [...(prev[chatId]?.messages || []), newMessage]
      }
    }));
  };

  const handleSendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const currentChat = chatSessions[currentChatId];
    if (!currentChat) return;

    // Add user message
    addMessage(currentChatId, messageText, 'user');

    // Start typing indicator
    setIsTyping(true);

    const expand = EXPAND_REGEX.test(messageText);

    if (currentChat.instance) {
      try {
        const response = await sendMessageToGemini(currentChat.instance, messageText, { expand });

        // Realistic typing simulation
        const typingDelay = Math.min(1400 + (response.length * 4), 2800);
        await new Promise(resolve => setTimeout(resolve, typingDelay));

        addMessage(currentChatId, response, 'ai');
      } catch (error) {
        console.error('Error getting AI response:', error);
        addMessage(currentChatId,
          "I'm having trouble connecting right now, but I'm still here! You can explore my portfolio sections above to learn about my projects, or try asking me again in a moment.",
          'ai'
        );
        // Error handled
      } finally {
        setIsTyping(false);
      }
    } else {
      // Fallback mode - provide simple responses when API is not available
      setTimeout(() => {
        let response = "Thanks for your interest! The AI chat is currently unavailable. You can explore my full portfolio above to learn about OpenSignl, Triage AI, and my Oracle AI work. Feel free to contact me directly to discuss opportunities!";

        // Simple keyword-based responses for fallback mode
        const lowerMessage = messageText.toLowerCase();
        if (lowerMessage.includes('triaged') || lowerMessage.includes('triage ai')) {
          response = "[Triage AI](https://triagedai.com) is a full-stack AI triage app serving **1,000+ users**. Built with Perplexity sonar-pro, React, TypeScript, and PostgreSQL-backed conversation memory. Achieved a **40% relevance lift** over baseline and replaced AWS Comprehend with a client-side sentiment model. Check it out in my Work section above.";
        } else if (lowerMessage.includes('signl') || lowerMessage.includes('opensignl')) {
          response = "[OpenSignl](https://www.opensignl.com/) is a research-backed content intelligence platform I founded. An agentic pipeline scans Reddit, X, LinkedIn, and news daily, surfaces ranked audience signals with verbatim evidence, and generates platform-native posts validated by an LLM-as-judge audit layer. Multi-model inference with graceful degradation and automatic failover.";
        } else if (lowerMessage.includes('safety') || lowerMessage.includes('eval') || lowerMessage.includes('oracle')) {
          response = "At Oracle, I built an end-to-end AI safety evaluation framework from scratch — reverse-engineered the chatbot API from HAR files, built a custom injection framework, and implemented LLM-as-judge scoring. The framework flagged safety gaps across **80% of stress-test scenarios** and the findings were presented to VP. The insights drove a redesign that improved groundedness by **90%**.";
        } else if (lowerMessage.includes('research') || lowerMessage.includes('brain') || lowerMessage.includes('tumor')) {
          response = "My [brain tumor segmentation research](https://github.com/AbdarrahmanAyyaz/TumorSegmentation/blob/main/README.md) used a U-Net architecture on the BraTS dataset (T1, T1CE, T2, FLAIR modalities) with systematic hyperparameter tuning. I presented findings at the SFSU–Genentech partnership symposium to hundreds of engineers and industry professionals.";
        } else if (lowerMessage.includes('skills') || lowerMessage.includes('technology') || lowerMessage.includes('tech')) {
          response = "I work with AI technologies like OpenAI and LangChain, frontend with React & Tailwind (4+ years), cloud platforms especially Oracle Cloud Infrastructure, and I'm currently exploring Vector DBs and RAG optimization. Check out my Skills section for the full breakdown!";
        } else if (lowerMessage.includes('collaborate') || lowerMessage.includes('work together') || lowerMessage.includes('hire')) {
          response = "I'd love to collaborate! I'm available for AI development projects, cloud infrastructure work, and frontend development. Feel free to reach out through my Contact form above or email me directly. Let's build something amazing together! 🚀";
        } else if (lowerMessage.includes('experience') || lowerMessage.includes('background')) {
          response = "I have **4+ years** of professional development experience, specializing in AI integration and cloud platforms. I've worked with React/TypeScript extensively, built production AI systems, and have experience with Oracle Cloud Infrastructure. My background spans both frontend development and machine learning research.";
        } else if (lowerMessage.includes('education') || lowerMessage.includes('degree') || lowerMessage.includes('study')) {
          response = "I graduated from San Francisco State University with a B.S. in Computer Science (3.7 GPA, Dean's List, Genentech-PINC Scholarship). My research focused on brain tumor segmentation using U-Net on the BraTS dataset, presented at the SFSU–Genentech partnership symposium.";
        } else if (lowerMessage.includes('location') || lowerMessage.includes('where') || lowerMessage.includes('remote')) {
          response = "I'm based in [Location] and open to both remote and on-site opportunities. I have experience working with distributed teams and am comfortable with various collaboration tools.";
        } else if (lowerMessage.includes('contact') || lowerMessage.includes('email') || lowerMessage.includes('reach')) {
          response = "You can reach me through the quick action buttons above! 📧 Email me directly, connect on LinkedIn, or check out my GitHub. I typically respond within 24 hours and love discussing new opportunities and interesting projects.";
        } else if (lowerMessage.includes('portfolio') || lowerMessage.includes('website') || lowerMessage.includes('projects')) {
          response = "This portfolio showcases my key work: **OpenSignl** (founder, live content intelligence SaaS), **AI Safety Eval Framework** (flagged safety gaps across 80% of stress-test scenarios at Oracle, 90% groundedness lift), **Triage AI** (1,000+ users, 40% relevance lift), and production AI systems I've built at Oracle.";
        }

        addMessage(currentChatId, response, 'ai');
        setIsTyping(false);
      }, 1500);
    }
  };

  const handleCopyMessage = async (message) => {
    try {
      await navigator.clipboard.writeText(message);
      // Could add toast notification here
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleLikeMessage = (message) => {
    // Analytics tracking could go here
    console.log('Message appreciated:', message.substring(0, 50) + '...');
  };

  const handleCategoryClick = (categoryKey) => {
    setExpandedCategory(expandedCategory === categoryKey ? null : categoryKey);
  };

  const handleQuestionClick = (question) => {
    setExpandedCategory(null);

    // Auto-send the message directly without filling input
    setTimeout(() => {
      handleSendMessage(question);
    }, 100);
  };

  // Close expanded category when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setExpandedCategory(null);
    };

    if (expandedCategory) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [expandedCategory]);

  // Track mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle ESC key for focus mode
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isFocusMode) {
        onToggleFocus?.();
      }
    };

    if (isFocusMode) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isFocusMode, onToggleFocus]);

  const currentChat = chatSessions[currentChatId];
  const currentMessages = currentChat?.messages || [];

  return (
    <div className="enhanced-ai-chat w-full max-w-none mx-auto px-2 sm:px-4 md:px-6 h-full">
      <div
        className="backdrop-blur-xl border rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl h-full flex flex-col
                   bg-surface/80 border-border"
      >
        
        {/* Chat Header with Controls */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="chat-header p-3 sm:p-4 md:p-6 backdrop-blur-sm border-b relative z-20
                     bg-surface/80 border-border"
        >
          {/* Header Controls - Top Right */}
          <div className="absolute top-2 right-2 sm:top-3 md:top-4 sm:right-3 md:right-4 flex items-center gap-1 sm:gap-2">
            {/* Expansion Controls */}
            {(onToggleSize || onToggleFocus) && (
              <div className="flex items-center gap-1">
                {onToggleSize && (
                  <button
                    onClick={onToggleSize}
                    className="p-1.5 sm:p-2 rounded-lg border transition-all duration-200 min-w-[40px] min-h-[40px] flex items-center justify-center
                              bg-surface text-text border-border hover:bg-accent/10 hover:border-accent/40"
                    title={chatSize === 'fullscreen' ? 'Minimize chat' : 'Expand chat'}
                  >
                    {getChatIcon && React.createElement(getChatIcon(), { size: 14 })}
                  </button>
                )}
                {onToggleFocus && (
                  <button
                    onClick={onToggleFocus}
                    className="p-1.5 sm:p-2 rounded-lg border transition-all duration-200 min-w-[40px] min-h-[40px] flex items-center justify-center
                              bg-surface text-text border-border hover:bg-accent/10 hover:border-accent/40"
                    title="Focus mode"
                  >
                    <Focus size={14} />
                  </button>
                )}
              </div>
            )}

          </div>

          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-full border-2 border-accent/30 flex-shrink-0 overflow-hidden"
              style={{
                boxShadow: `0 0 30px rgba(100, 100, 100, 0.3)`
              }}
            >
              <img
                src={profileImage}
                alt="Abdarrahman Ayyaz"
                className="w-full h-full object-cover"
                style={{ objectPosition: '70% 70%', transform: 'scale(1.3)' }}
              />
            </div>
            <div className="flex-1 min-w-0 pr-14 sm:pr-16 md:pr-20">
              <h2 className="text-base sm:text-lg md:text-2xl font-bold bg-gradient-to-r from-text to-text/80 bg-clip-text text-transparent truncate">
                AI Abdarrahman
              </h2>
              <p className="text-muted mt-0.5 sm:mt-1 text-xs sm:text-sm">
                Cloud & AI Engineer · Founder of OpenSignl
              </p>
            </div>
          </div>
        </motion.div>

        {/* Chat Messages Area */}
        <div
          className="chat-messages flex-1 overflow-y-auto p-4 sm:p-5 md:p-6 space-y-4 sm:space-y-5 md:space-y-6 relative z-10 max-h-[65vh]
                     bg-gradient-to-b from-bg/40 to-transparent"
          role="log"
          aria-live="polite"
          aria-atomic="false"
          aria-relevant="additions text"
          aria-label="Chat conversation"
        >
          <AnimatePresence mode="popLayout">
            {currentMessages.map((message) => (
              <ChatMessageSimple
                key={message.id}
                message={message.text}
                type={message.type}
                timestamp={message.timestamp}
                onCopy={handleCopyMessage}
                onLike={handleLikeMessage}
              />
            ))}
          </AnimatePresence>
          
          {isTyping && (
            <TypingIndicator />
          )}
          <div ref={messagesEndRef} />
        </div>


        {/* Show Quick Action Pills above input only when no conversation has started */}
        {currentMessages.length <= 1 && (
          <div className="flex-shrink-0 p-4 sm:p-5 border-t border-border">
            <div className="space-y-3 sm:space-y-4">
              {/* Category Pills */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(categoryData).map(([key, category]) => {

                  return (
                    <motion.button
                      key={key}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(key);
                      }}
                      className="
                        relative px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border transition-all duration-200
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                        touch-manipulation min-h-[44px]
                        bg-surface text-text border-border
                        hover:bg-accent/10 hover:border-accent/40
                      "
                      whileHover={{
                        scale: 1.02,
                        transition: { duration: 0.2, ease: "easeOut" }
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                        <span className="text-sm">{category.icon}</span>
                        <span className="font-medium text-xs sm:text-sm truncate">{category.label}</span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Expanded Questions */}
              <AnimatePresence>
                {expandedCategory && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                      {categoryData[expandedCategory].questions.map((question, index) => (
                        <motion.button
                          key={index}
                          onClick={() => handleQuestionClick(question)}
                          className="
                            px-3 py-2.5 rounded-lg border text-xs sm:text-sm text-left transition-all duration-200
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                            touch-manipulation min-h-[44px]
                            bg-surface/50 text-text border-border/60
                            hover:bg-accent/10 hover:border-accent/50 hover:text-text
                          "
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          {question}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Chat Input */}
        <div className="flex-shrink-0 mt-4 mb-2">
          <ChatInput
            onSend={handleSendMessage}
            disabled={isTyping}
            placeholder="Ask about my projects, skills, or experience..."
            theme={theme}
            onFocus={onInputFocus}
            onInputChange={onInputChange}
          />
        </div>

        {/* Show Quick Action Pills below input when conversation has started - Hide on mobile */}
        {currentMessages.length > 1 && !isMobile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="flex-shrink-0 p-4 sm:p-5 border-t border-border"
          >
            <div className="space-y-3 sm:space-y-4">
              {/* Category Pills */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(categoryData).map(([key, category]) => {

                  return (
                    <motion.button
                      key={key}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(key);
                      }}
                      className="
                        relative px-2 py-2 sm:px-3 sm:py-2.5 rounded-lg border transition-all duration-200
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                        touch-manipulation min-h-[40px]
                        bg-surface text-text border-border
                        hover:bg-accent/10 hover:border-accent/40
                      "
                      whileHover={{
                        scale: 1.02,
                        transition: { duration: 0.2, ease: "easeOut" }
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-center gap-1 sm:gap-1.5">
                        <span className="text-xs">{category.icon}</span>
                        <span className="font-medium text-xs truncate">{category.label}</span>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Expanded Questions */}
              <AnimatePresence>
                {expandedCategory && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                      {categoryData[expandedCategory].questions.map((question, index) => (
                        <motion.button
                          key={index}
                          onClick={() => handleQuestionClick(question)}
                          className="
                            px-2 py-2 sm:px-3 sm:py-2.5 rounded-lg border text-xs text-left transition-all duration-200
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                            touch-manipulation min-h-[40px]
                            bg-surface/50 text-text border-border/60
                            hover:bg-accent/10 hover:border-accent/50 hover:text-text
                          "
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          {question}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EnhancedAIChatWorking;
